import { createModel } from "@rematch/core";
import { client } from "../shared/apiClient";
import {
  IKioskCustomizationDto,
  IKioskOptionsDto,
  IKioskSessionInfoSheetDto,
  IKioskSessionsDto,
  ISortDto,
  ISentInfoSheet,
  IFailedEmailsList,
  ISentVideoDirection,
  IResendStats
} from "../shared/dto/IKioskDto";
import { IApiResponseError } from "../shared/dto/IErrorDto";
import i18next from "i18next";
import { IAuditionQuestion } from "../interfaces/IAuditionQuestion";
import { IAuditionAnswer } from "../interfaces/IAuditionAnswer";
import { IActorConflict } from "../interfaces/IActorConflicts";
import {
  ActorProfileDetailLists,
  IAuditionInfoSheetDto
} from "../interfaces/IAuditionInfoSheetDto";
import { IKioskSessionRoles } from "../interfaces/IKioskSessionRoles";
import { IVideoDirection } from "../interfaces/IVideoDirections";
import { IKioskSessionInfo } from "../interfaces/IKioskSessionInfo";
import { IMedia } from "../interfaces/IMedia";
import { IKeyValueVideoDirection } from "../interfaces/IKeyValueVideoDirection";
import { IKeyValueAuditionQuestion } from "../interfaces/IKeyValueAuditionQuestion";
import { ISessionTemplateInfoSheet } from "../interfaces/ISessionTemplateInfoSheet";
import { IKioskSessionAuditionsDto } from "../interfaces/IKioskSessionAuditionsDto";

// TODO: refac into models per api...
const kiosk = createModel({
  state: {
    loading: false as boolean,
    sessions: [] as Array<IKioskSessionsDto>,
    sessionInfoSheets: [] as Array<IKioskSessionInfoSheetDto>,
    sessionAuditions: null as IKioskSessionAuditionsDto,
    sessionTemplateInfoSheet: null as ISessionTemplateInfoSheet,
    sortField: "auditionDate" as string,
    sortDescending: "desc" as string,
    scheduleId: 0,
    selectedKioskOption: null,
    kioskCustomizationInfo: null as IKioskCustomizationDto,
    kioskLogo: "" as string,
    auditionQuestions: [] as IAuditionQuestion[],
    auditionAnswers: [] as IAuditionAnswer[],
    sentInfoSheets: [] as ISentInfoSheet[],
    infoSheetOptions: {} as ActorProfileDetailLists,
    auditionInfoSheet: {} as IAuditionInfoSheetDto,
    infoSheetConflicts: [] as IActorConflict[],
    sessionRoles: [] as IKioskSessionRoles[],
    videoDirections: [] as IVideoDirection[],
    sessionInfo: {} as IKioskSessionInfo,
    media: {} as IMedia,
    medias: [] as IMedia[],
    dictVideoDirection: {} as IKeyValueVideoDirection,
    failedEmailsList: [] as Array<IFailedEmailsList>,
    sentAllVideoDirections: [] as ISentVideoDirection[],
    dictAuditionQuestion: {} as IKeyValueAuditionQuestion,
    switchAccountUserId: -1 as number,
    switchAccountUserType: 3 as number,
    showFailedEmailModal: false as boolean,
    showResendLinkModal: {
      show: false as boolean,
      scheduleId: 0 as number
    },
    resendStats: {} as IResendStats,
    showInfoSheetsEnabledModal: false as boolean,
    showLocationEnabledModal: {
      show: false as boolean,
      scheduleId: 0 as number
    },
    lockAuditionTimes: false as boolean
  },
  reducers: {
    setLoading: (state: any, loading: boolean) => ({
      ...state,
      loading
    }),
    setKioskSessions: (state: any, sessions: IKioskSessionsDto[]) => ({
      ...state,
      sessions,
    }),
    setSwitchAccountParams: (
      state: any,
      switchAccountUserId: number,
      switchAccountUserType: number
    ) => ({ ...state, ...{ switchAccountUserId, switchAccountUserType } }),
    setKioskSessionRoles: (state: any, sessionRoles: IKioskSessionRoles[]) => ({
      ...state,
      sessionRoles,
    }),
    setKioskSessionInfo: (state: any, sessionInfo: IKioskSessionInfo) => ({
      ...state,
      sessionInfo,
    }),
    setKioskSessionInfoSheetsEnabled: (state: any, enabled: boolean) => {
      return {
        ...state,
        sessionInfo: {
          ...state.sessionInfo,
          infoSheetsEnabled: enabled
        }
      };
    },
    setKioskSessionInfoLockAuditionTimesEnabled: (state: any, enabled: boolean) => {
      return {
        ...state,
        sessionInfo: {
          ...state.sessionInfo,
          lockAuditionTimes: enabled
        }
      };
    },
    setAuditionQuestions: (
      state: any,
      auditionQuestions: IAuditionQuestion[]
    ) => ({
      ...state,
      auditionQuestions,
    }),
    setVideoDirections: (state: any, videoDirections: IVideoDirection[]) => ({
      ...state,
      videoDirections,
    }),
    setDictVideoDirections: (state: any, { videoDirections, scheduleId }) => {
      // move to helper
      let dictVideoDirection: IKeyValueVideoDirection = {
        ...state.dictVideoDirection,
      };

      if (videoDirections.length > 0)
        videoDirections
          .map((x: IVideoDirection) => x.scheduleId)
          .forEach((sId: number) => {
            dictVideoDirection[sId] = videoDirections.filter(
              (x: IVideoDirection) => x.scheduleId === sId
            );
          });
      else if (videoDirections.length === 0 && scheduleId > 0)
        dictVideoDirection[scheduleId] = videoDirections;

      return {
        ...state,
        dictVideoDirection,
      };
    },
    setDictAuditionQuestions: (
      state: any,
      { auditionQuestions, scheduleId }
    ) => {
      // move to helper
      let dictAuditionQuestion: IKeyValueVideoDirection = {
        ...state.dictAuditionQuestion,
      };

      if (auditionQuestions.length > 0)
        auditionQuestions
          .map((x: IAuditionQuestion) => x.scheduleId)
          .forEach((sId: number) => {
            dictAuditionQuestion[sId] = auditionQuestions.filter(
              (x: IAuditionQuestion) => x.scheduleId === sId
            );
          });
      else if (auditionQuestions.length === 0 && scheduleId > 0)
        dictAuditionQuestion[scheduleId] = auditionQuestions;

      return {
        ...state,
        dictAuditionQuestion,
      };
    },
    setMedia: (state: any, media: IMedia) => ({
      ...state,
      media,
    }),
    setMedias: (state: any, medias: IMedia[]) => ({
      ...state,
      medias,
    }),
    setArraySentAllVideoDirection: (
      state: any,
      sentAllVideoDirections: ISentVideoDirection[]
    ) => ({
      ...state,
      sentAllVideoDirections,
    }),
    setSentAllVideoDirection: (
      state: any,
      sentAllVideoDirection: ISentVideoDirection
    ) => {
      let sentDirections: ISentVideoDirection[] = [
        ...state.sentAllVideoDirections,
      ];

      let exist = null;
      if (sentDirections.length > 0) {
        exist = sentDirections.find(
          (x) => x.scheduleId === sentAllVideoDirection.scheduleId
        );
      }

      if (exist) {
        exist.hasSentAll = sentAllVideoDirection.hasSentAll;
        sentDirections = sentDirections.map((sentVideoDirection) => {
          if (
            sentVideoDirection.scheduleId === sentAllVideoDirection.scheduleId
          )
            sentVideoDirection.hasSentAll = sentAllVideoDirection.hasSentAll;
          return sentVideoDirection;
        });
      } else {
        sentDirections.push(sentAllVideoDirection);
      }

      return {
        ...state,
        sentAllVideoDirections: sentDirections,
      };
    },
    setInfoSheetConflicts: (
      state: any,
      infoSheetConflicts: IActorConflict[]
    ) => ({
      ...state,
      infoSheetConflicts,
    }),
    setAuditionAnswers: (state: any, auditionAnswers: IAuditionAnswer[]) => ({
      ...state,
      auditionAnswers,
    }),
    setInfoSheetOptions: (
      state: any,
      infoSheetOptions: ActorProfileDetailLists
    ) => ({
      ...state,
      infoSheetOptions
    }),
    setAuditionInfoSheet: (
      state: any,
      auditionInfoSheet: IAuditionInfoSheetDto
    ) => ({
      ...state,
      auditionInfoSheet,
    }),
    setSentInfoSheet: (state: any, sentInfoSheets: ISentInfoSheet[]) => ({
      ...state,
      sentInfoSheets,
    }),
    setKioskSessionAuditions: (
      state: any,
      sessionAuditions: IKioskSessionAuditionsDto
    ) => ({
      ...state,
      sessionAuditions,
    }),
    setsessionTemplateInfoSheet: (
      state: any,
      sessionTemplateInfoSheet: ISessionTemplateInfoSheet
    ) => ({
      ...state,
      sessionTemplateInfoSheet,
    }),
    setCurrentScheduleId: (state: any, scheduleId: number) => ({
      ...state,
      scheduleId,
    }),
    setSortFieldDescending: (
      state,
      { sortField, sortDescending }: ISortDto
    ) => ({
      ...state,
      sortField,
      sortDescending,
    }),
    setSelectedKioskOption: (state, selectedKioskOption: IKioskOptionsDto) => ({
      ...state,
      selectedKioskOption,
    }),
    setCustomizationInfo: (
      state,
      kioskCustomizationInfo: IKioskCustomizationDto
    ) => ({
      ...state,
      kioskCustomizationInfo,
    }),
    setKioskLogo: (state, kioskLogo: string) => ({
      ...state,
      kioskLogo,
    }),
    setFailedEmailsList: (state, failedEmailsList: IFailedEmailsList[]) => ({
      ...state,
      failedEmailsList,
    }),
    setShowFailedEmailModal: (state, showFailedEmailModal: boolean) => ({
      ...state,
      showFailedEmailModal
    }),
    setShowResendLinkModal: (state: any, show: boolean, scheduleId?: number) => ({
      ...state,
      showResendLinkModal: { show, scheduleId }
    }),
    setResendStats: (state: any, resendStats: IResendStats) => ({
      ...state,
      resendStats
    }),
    setShowInfoSheetsEnabledModal: (state: any, show: boolean) => ({
      ...state,
      showInfoSheetsEnabledModal: show
    }),
    setLocationEnabledModal: (state: any, show: boolean, scheduleId?: number) => ({
      ...state,
      showLocationEnabledModal: { show, scheduleId }
    })
  },
  effects: (dispatch: any) => ({
    async getKioskSessions(filter) {
      await client.api.kiosk
        .getKioskSessions(filter, null, null)
        .then((resp: any) => {
          const videoDirections = resp.data.videoDirections;
          const sessions = resp.data.sessions;
          const auditionQuestions = resp.data.auditionQuestions;
          const sentAllVideoDirections = resp.data.sentVideoDirections;
          this.setDictAuditionQuestions({ auditionQuestions });
          this.setDictVideoDirections({ videoDirections });
          this.setArraySentAllVideoDirection(sentAllVideoDirections);
          this.setKioskSessions(sessions);
          this.setSwitchAccountParams(
            resp.data.switchAccountUserId,
            resp.data.switchAccountUserType
          );
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getKioskSessionRoles(id) {
      await client.api.kiosk
        .getSessionRoles(id)
        .then((resp: any) => {
          let roles: IKioskSessionRoles[] = resp.data;
          this.setKioskSessionRoles(roles);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getKioskSessionInfo(id) {
      await client.api.kiosk
        .getKioskSessionInfo(id)
        .then((resp: any) => {
          let sessionInfo: IKioskSessionInfo = resp.data;
          this.setKioskSessionInfo(sessionInfo);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getSessionAuditions(id) {
      return await client.api.kiosk
        .getSessionAuditions(id)
        .then((resp: any) => {
          let sessionAuditions: IKioskSessionAuditionsDto = resp.data;
          this.setKioskSessionAuditions(sessionAuditions);
          this.setCurrentScheduleId(id);
          return sessionAuditions;
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getSessionAuditionsOptReorder({ scheduleId, reorder = false }) {
      return await client.api.kiosk
        .getSessionAuditions(scheduleId, reorder)
        .then((resp: any) => {
          let sessionAuditions: IKioskSessionAuditionsDto = resp.data;
          this.setKioskSessionAuditions(sessionAuditions);
          this.setCurrentScheduleId(scheduleId);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getSentAuditionInfoSheet(ids: number[]) {
      const joinedIds = "scheduleIds=" + ids.join("&scheduleIds=");
      await client.api.infosheet
        .getSentAuditionInfoSheet(joinedIds)
        .then((resp: any) => {
          let data: ISentInfoSheet = resp.data;
          this.setSentInfoSheet(data);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getSentAllVideoDirections(scheduleId) {
      await client.api.videoDirection
        .getSentAllVideoDirections(scheduleId)
        .then((resp: any) => {
          let data: ISentVideoDirection = resp.data;
          this.setSentAllVideoDirection(data);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getSessionTemplateInfoSheet(id) {
      await client.api.infosheet
        .getSessionTemplateInfoSheet(id)
        .then((resp: any) => {
          let sessionTemplateInfoSheet: ISessionTemplateInfoSheet = resp.data;
          this.setsessionTemplateInfoSheet(sessionTemplateInfoSheet);
          this.setCurrentScheduleId(id);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getSessionTemplateInfoSheetWithAuditionSchedule(idObj) {
      await client.api.infosheet
        .getSessionTemplateInfoSheetWithAuditionSchedule(idObj)
        .then((resp: any) => {
          let sessionTemplateInfoSheet: ISessionTemplateInfoSheet = resp.data;
          this.setsessionTemplateInfoSheet(sessionTemplateInfoSheet);
          this.setCurrentScheduleId(idObj);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getAuditionQuestions(id) {
      await client.api.auditionqa
        .getAuditionQuestions(id)
        .then((resp: any) => {
          let audQuestions: IAuditionQuestion[] = resp.data;
          this.setAuditionQuestions(audQuestions);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },
    async getAuditionAnswers(id) {
      await client.api.auditionqa
        .getAuditionAnswers(id)
        .then((resp: any) => {
          let answers: IAuditionAnswer[] = resp.data;
          this.setAuditionAnswers(answers);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getVideoDirections(scheduleId) {
      await client.api.videoDirection
        .get(scheduleId)
        .then((resp: any) => {
          let videoDirections: IVideoDirection[] = resp.data;
          this.setDictVideoDirections({ videoDirections, scheduleId });
          this.setVideoDirections(videoDirections); // TODO: tmp needed for Session Auditions
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getMedia(id) {
      await client.api.pm
        .getMedia(id)
        .then((resp: any) => {
          let media: IMedia = resp.data;
          this.setMedia(media);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getMultipleMedia(ids) {
      await client.api.pm
        .getMultipleMedia(ids)
        .then((resp: any) => {
          let medias: IMedia[] = resp.data;
          this.setMedias(medias);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getInfoSheetOptions() {
      await client.api.infosheet
        .getInfoSheetOptions()
        .then((resp: any) => {
          dispatch.kiosk.setInfoSheetOptions(resp.data);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getAuditionInfoSheet({ infoSheetId, auditionScheduleId }) {
      await client.api.infosheet
        .getAuditionInfoSheet(infoSheetId, auditionScheduleId)
        .then((resp: any) => {
          let auditionInfoSheet: IAuditionInfoSheetDto = resp.data;
          this.setAuditionInfoSheet(auditionInfoSheet);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async createAuditionInfoSheet(auditionScheduleId) {
      return await client.api.infosheet
        .createAuditionInfoSheet(auditionScheduleId)
        .then((resp: any) => resp.data)
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async getInfoSheetConflicts(id) {
      await client.api.infosheet
        .getInfoSheetConflicts(id)
        .then((resp: any) => {
          let o: IActorConflict = resp.data;
          this.setInfoSheetConflicts(o);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    // async addEditSessionInfoSheet(sessionInfoSheet) {
    //   await client.api.infosheet
    //     .addEditSessionInfoSheet(sessionInfoSheet)
    //     .then((resp: any) => {})
    //     .catch((resp: any) => {
    //       onErrorRedirect(resp);
    //       let respError: IApiResponseError = resp.response.data;
    //       if (respError.error) {
    //         dispatch.alert.showAlert("error", respError);
    //       }
    //     });
    // },

    async setInfoSheetsEnabled({ scheduleId, enabled }) {
      try {
        const response = await client.api.kiosk.enableInfoSheet(
          scheduleId,
          enabled
        );
        if (response.status >= 200 && response.status < 300) {
          dispatch.kiosk.setKioskSessionInfoSheetsEnabled(enabled);
        }
      } catch (e) {
        onErrorRedirect(e);
        const customErr: any = e;
        let respError: IApiResponseError = customErr.response && customErr.response.data ? customErr.response.data : {};
        if (respError.error) {
          dispatch.alert.showAlert("error", respError);
        }
      }
    },

    async setLockAuditionTimes({ scheduleId, enabled }) {
      try {
        const response = await client.api.kiosk.enableTimeLock(
          scheduleId,
          enabled
        );
        if (response.status >= 200 && response.status < 300) {
          dispatch.kiosk.setKioskSessionInfoLockAuditionTimesEnabled(enabled);
        }
      } catch (e) {
        onErrorRedirect(e);
        const customErr: any = e;
        let respError: IApiResponseError = customErr.response && customErr.response.data ? customErr.response.data : {};
        if (respError.error) {
          dispatch.alert.showAlert("error", respError);
        }
      }
    },

    async sendInfoSheet(scheduleId) {
      try {
        const response = await client.api.infosheet.sendInfoSheet(scheduleId);
        if (
          response.data &&
          response.data.reports &&
          response.data.reports.length > 0
        ) {
          const failedEmails = getFailedEmailsOnItemSend(response.data.reports);
          if (failedEmails.length > 0) {
            dispatch.kiosk.setFailedEmailsList(failedEmails);
            return false;
          } else {
            return true;
          }
        } else {
          dispatch.alert.showAlert(
            "error",
            i18next.t("Error! No emails were sent.")
          );
        }
      } catch (e) {
        onErrorRedirect(e);
      }
    },

    async sendVideoDirections(scheduleId) {
      try {
        const response = await client.api.videoDirection.sendVideoDirections(scheduleId);
        if (
          response.data &&
          response.data.reports &&
          response.data.reports.length > 0
        ) {
          const failedEmails = getFailedEmailsOnItemSend(response.data.reports);
          if (failedEmails.length > 0) {
            dispatch.kiosk.setFailedEmailsList(failedEmails);
            return false;
          } else {
            dispatch.kiosk.setSentAllVideoDirection({
              scheduleId: scheduleId,
              hasSentAll: true
            } as ISentVideoDirection);
            return true;
          }
        } else {
          dispatch.alert.showAlert(
            "error",
            i18next.t("Error! No emails were sent.")
          );
        }
      } catch (e) {
        onErrorRedirect(e);
      }
    },

    async sendZoomEmail(scheduleId) {
      try {
        const response = await client.api.kiosk.sendZoomEmail(scheduleId);
        if (
          response.data &&
          response.data.reports &&
          response.data.reports.length > 0
        ) {
          const failedEmails = getFailedEmailsOnItemSend(response.data.reports);
          if (failedEmails.length > 0) {
            dispatch.kiosk.setFailedEmailsList(failedEmails);
            return false;
          } else {
            return true;
          }
        } else {
          dispatch.alert.showAlert(
            "error",
            i18next.t("Error! No emails were sent.")
          );
        }
      } catch (e) {
        onErrorRedirect(e);
      }
    },

    async addAuditionQuestions(auditionQuestions: IAuditionQuestion[], state) {
      await client.api.auditionqa
        .addAuditionQuestions(auditionQuestions, state.kiosk.scheduleId)
        .then((resp: any) => { })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async addAuditionAnswers({ infoSheetId, answersToAdd }, state) {
      await client.api.auditionqa
        .addAuditionAnswers(answersToAdd, infoSheetId)
        .then((resp: any) => { })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async addVideoDirections(videoDirections, state) {
      await client.api.videoDirection
        .add(videoDirections)
        .then((resp: any) => {
          //dispatch.kiosk.getVideoDirections(videoDirections[0].scheduleId);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async updateAuditionAnswers({ infoSheetId, answersToUpdate }, state) {
      await client.api.auditionqa
        .updateAuditionAnswers(answersToUpdate, infoSheetId)
        .then((resp: any) => { })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async updateAuditionQuestions(
      auditionQuestions: IAuditionQuestion[],
      state
    ) {
      await client.api.auditionqa
        .updateAuditionQuestions(auditionQuestions, state.kiosk.scheduleId)
        .then((resp: any) => { })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },

    async updateAuditionInfoSheet(infoSheet: IAuditionInfoSheetDto, state) {
      await client.api.infosheet
        .updateAuditionInfoSheet(infoSheet)
        .then((resp: any) => { })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },
    async addEditCheckIn(checkInDto, state) {
      // optimistic update
      let sessionAuditions: IKioskSessionAuditionsDto =
        state.kiosk.sessionAuditions;
      let i = sessionAuditions.auditionCheckIns.findIndex(
        (x) => x.checkInId === checkInDto.checkInId
      );
      sessionAuditions.auditionCheckIns[i] = checkInDto;
      this.setKioskSessionAuditions(sessionAuditions);

      await client.api.checkin
        .addEditCheckIn(checkInDto)
        .then(() => {
          dispatch.kiosk.getSessionAuditions(state.kiosk.scheduleId);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },
    async deleteCheckIn(checkInId: number, state) {
      // optimistic update
      let sessionAuditions: IKioskSessionAuditionsDto =
        state.kiosk.sessionAuditions;
      let i = sessionAuditions.auditionCheckIns.findIndex(
        (x) => x.checkInId === checkInId
      );
      sessionAuditions.auditionCheckIns.splice(i, 1);
      this.setKioskSessionAuditions(sessionAuditions);

      await client.api.checkin
        .deleteCheckIn(checkInId)
        .then(() => {
          dispatch.kiosk.getSessionAuditions(state.kiosk.scheduleId);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },
    async deleteAudition({ auditionScheduleId, scheduleId, skipRefresh }, state) {
      return await client.api.kiosk
        .deleteAudition(auditionScheduleId, scheduleId)
        .then(async () => {
          if (skipRefresh)
            return true;

          await dispatch.kiosk.getSessionAuditions(state.kiosk.scheduleId); 
          return true;
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }

          //refresh list
          dispatch.kiosk.getSessionAuditions(state.kiosk.scheduleId);
          return false;
        });
    },
    async deleteVideoDirection({ videoDirectionId, scheduleId }, state) {
      await client.api.videoDirection
        .delete(videoDirectionId)
        .then(() => {
          dispatch.kiosk.getVideoDirections(scheduleId);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },
    async restoreAudition({ auditionScheduleId, scheduleId, skipRefresh }, state) {
      await client.api.kiosk
        .restoreAudition(auditionScheduleId, scheduleId)
        .then(async () => {
          if (skipRefresh)
            return true;

          await dispatch.kiosk.getSessionAuditions(state.kiosk.scheduleId);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },
    async getKioskCustomizationInfo(_, state) {
      await client.api.managekiosk
        .getKioskCustomizationInfo()
        .then((resp: any) => {
          let info: IKioskCustomizationDto = resp.data;
          this.setCustomizationInfo(info);
          this.setKioskLogo(info.kioskBackgroundFile);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },
    async saveKioskManagementInfo(file) {
      const formData = new FormData();
      formData.append("file", file);
      await client.api.managekiosk
        .saveKioskCustomizationImg(formData)
        .then(() => {
          dispatch.alert.showAlert("success", i18next.t("Save successful."));
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },
    async deleteKioskManagementInfo(file) {
      await client.api.managekiosk
        .deleteKioskManagementImg()
        .then(() => {
          dispatch.alert.showAlert("success", i18next.t("Delete successful."));
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },
    async getResendStats(scheduleId) {
      await client.api.kiosk
        .resendStats(scheduleId)
        .then((resp: any) => {
          const resendStats: IResendStats = resp.data;
          dispatch.kiosk.setResendStats(resendStats);
        })
        .catch((resp: any) => {
          onErrorRedirect(resp);
          let respError: IApiResponseError = resp.response.data;
          if (respError.error) {
            dispatch.alert.showAlert("error", respError);
          }
        });
    },
    async resendAuditionLink(resendDetails) {
      dispatch.kiosk.setLoading(true);
      try {
        const response = await client.api.kiosk.resendEmail(resendDetails);
        if (
          response.data &&
          response.data.reports &&
          response.data.reports.length > 0
        ) {
          const failedEmails = getFailedEmailsOnItemSend(response.data.reports);
          if (failedEmails.length > 0) {
            dispatch.kiosk.setFailedEmailsList(failedEmails);
            return false;
          } else {
            return true;
          }
        } else {
          dispatch.alert.showAlert(
            "error",
            i18next.t("Error! No emails were sent.")
          );
        }
      } catch (e) {
        onErrorRedirect(e);
      } finally {
        dispatch.kiosk.setLoading(false);
      }
    },
    async syncPushToSam({ scheduleId }, state) {
      try {
        const response = await client.api.kiosk.syncPushToSam(scheduleId);
        if (response.status >= 200 && response.status < 300) {
          dispatch.alert.showAlert(
            "success",
            i18next.t("Your session is being copied to your SAM account. This may take several minutes depending on the size of your project.")
          );
        } else {
          dispatch.alert.showAlert(
            "error",
            i18next.t("Error! No new data is being copied to SAM. Please try again or contact our support.")
          );
        }
      } catch (e) {
        dispatch.alert.showAlert(
          "error",
          i18next.t("Error! No new data is being copied to SAM. Please try again or contact our support.")
        );
      }

    },
  }),

});

export default kiosk;

const onErrorRedirect = (resp: any) => {
  if (!resp.response || resp.response.status === 500) {
    window.location.replace(`${client.api.paths.redirect500}`);
  } else if (resp.response.status === 401 || resp.response.status === 403) {
    window.location.replace(`${client.api.paths.redirect403}`);
  }
};

const getFailedEmailsOnItemSend = (reports: any) => {
  return reports
    .filter((report: any) => !report.success)
    .map((email: any) => ({
      name: email.actorFullName,
      emails: email.results.map(({ email }: any) => email)
    }));
};
